import * as React from 'react';
import { FC, useMemo } from 'react';

import { formatTime, t } from 'state/intl/translations';
import { usePickupTime } from 'state/order/hooks/use-pickup-time';
import { getLocalizedServiceMode } from 'utils/service-mode';

import { MinimalAccountOrder } from '../types';

import {
  ReceiptDate,
  ReceiptHeadInfoContainer,
  ReceiptInfo,
  ReceiptInfoDetail,
  ReceiptInfoDetailLabel,
  ReceiptInfoLabel,
  Title,
} from './receipt-head-info.styled';

const MinimalReceiptHeadInfo: FC<React.PropsWithChildren<{ order: MinimalAccountOrder }>> = ({
  order,
}) => {
  const message = t('orderDetailsHeading');
  const { pickupDate } = usePickupTime(order);
  const { formattedDate, formattedTime } = useMemo(() => {
    const orderDate = new Date(order.createdAt);
    const formattedOrderDate = orderDate.toLocaleString(t('localeDateTime'), {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    const formattedOrderTime = orderDate.toLocaleString('en-us', {
      hour: 'numeric',
      minute: 'numeric',
      timeZoneName: 'short',
    });

    return { formattedDate: formattedOrderDate, formattedTime: formattedOrderTime };
  }, [order.createdAt]);

  const { storeAddress, serviceMode } = order.cart;
  const localizedServiceMode = getLocalizedServiceMode(serviceMode);
  const orderNumberLabel = t('orderNumber');
  const orderTimeLabel = t('orderTime');
  const serviceModeLabel = t('method', {
    serviceMode: '',
  });
  const orderPickUpTimeLabel = t('pickUpTime');

  return (
    <>
      <Title accessibilityLabel={message}>{message}</Title>
      <ReceiptHeadInfoContainer>
        <ReceiptInfo>
          <ReceiptDate>{formattedDate}</ReceiptDate>
        </ReceiptInfo>
        {order.orderNumberFormatted ? (
          <ReceiptInfo accessibilityLabel={`${orderNumberLabel}:${order.orderNumberFormatted}`}>
            <ReceiptInfoLabel>{orderNumberLabel}:</ReceiptInfoLabel>
            <ReceiptInfoDetail>{order.orderNumberFormatted}</ReceiptInfoDetail>
          </ReceiptInfo>
        ) : null}
        <ReceiptInfo accessibilityLabel={`${orderTimeLabel}:${formattedTime}`}>
          <ReceiptInfoLabel>{orderTimeLabel}:</ReceiptInfoLabel>
          <ReceiptInfoDetail>
            <ReceiptInfoDetailLabel>{formattedTime}</ReceiptInfoDetailLabel>
          </ReceiptInfoDetail>
        </ReceiptInfo>

        <ReceiptInfo accessibilityLabel={`${serviceModeLabel}:${localizedServiceMode}`}>
          <ReceiptInfoLabel>{serviceModeLabel}</ReceiptInfoLabel>
          <ReceiptInfoDetail>
            <ReceiptInfoDetailLabel>{localizedServiceMode}</ReceiptInfoDetailLabel>
          </ReceiptInfoDetail>
        </ReceiptInfo>

        {!!order.fireOrderIn && (
          <ReceiptInfo accessibilityLabel={`${orderPickUpTimeLabel}:${formatTime(pickupDate)}`}>
            <ReceiptInfoLabel>{orderPickUpTimeLabel}:</ReceiptInfoLabel>
            <ReceiptInfoDetail>
              <ReceiptInfoDetailLabel>{formatTime(pickupDate)}</ReceiptInfoDetailLabel>
            </ReceiptInfoDetail>
          </ReceiptInfo>
        )}

        <ReceiptInfo>
          <ReceiptInfoLabel>{t('location')}:</ReceiptInfoLabel>
          <ReceiptInfoDetail>
            <ReceiptInfoDetailLabel testID="receipt-address">
              {storeAddress.addressLine1}
              {', '}
              {storeAddress.addressLine2 &&
                `${t('unitNumber', { unitNumber: storeAddress.addressLine2 })}, `}
              {storeAddress.city}
              {', '}
              {storeAddress.state}
              {', '}
              {storeAddress.zip}
            </ReceiptInfoDetailLabel>
          </ReceiptInfoDetail>
        </ReceiptInfo>
      </ReceiptHeadInfoContainer>
    </>
  );
};

export default MinimalReceiptHeadInfo;
